import { Equipment } from '../../types/project';

// One equipment table, two roles (docs/specs/traditional-calcs.md §8):
// 'proposed' rows are the Equipment tab — the new loads both engines evaluate;
// 'existing' rows are the Dwelling Profile inventory the traditional engine
// calculates from. Rows written before the phase-1 migration carry no
// load_role and are proposed by definition.
//
// Every consumer of proposed loads — the 220.87 engine, completion
// validation, the Equipment tab, the report — must go through these filters,
// or dwelling inventory silently inflates the new-load math.

export function proposedLoads(equipment: Equipment[]): Equipment[] {
  return equipment.filter((eq) => (eq.load_role ?? 'proposed') === 'proposed');
}

export function existingLoads(equipment: Equipment[]): Equipment[] {
  return equipment.filter((eq) => eq.load_role === 'existing');
}

// 'removed' rows are equipment taken out with this work. They are neither
// proposed nor existing, so both filters above already leave them out: the
// traditional engine never counts them (the optional method calculates the
// dwelling as it will be after the work), and the measured engine takes them
// as a separate list (measured22087.ts).
export function removedLoads(equipment: Equipment[]): Equipment[] {
  return equipment.filter((eq) => eq.load_role === 'removed');
}

/**
 * Whether a NEW load falls in Table 120.83's "new central electric resistance
 * space heating" row (80%) rather than "all other new loads" (50%). Strip
 * heat always does. A heat pump does from rules revision 4 unless the
 * preparer has said its rating excludes backup strip heat: the strips in an
 * air handler are central resistance heat, and a row whose rating includes
 * them cannot be split by the engine, so the whole row takes the higher rate.
 * Unanswered counts as included — the conservative reading, and completion
 * blocks until it is answered under 2026. Both 2026 engines call this.
 */
export function countsAsCentralResistanceHeat(eq: Equipment, calcRules: number): boolean {
  if (eq.equipment_type === 'Strip Heat') return true;
  return eq.equipment_type === 'Heat Pump' && calcRules >= 4 && eq.includes_backup_heat !== false;
}

/** Removed rows the preparer actually filled in — same blank rule as proposed. */
export function enteredRemovedLoads(equipment: Equipment[]): Equipment[] {
  return removedLoads(equipment).filter((eq) => !isBlankProposedRow(eq));
}

/**
 * A proposed row the preparer added but never filled in.
 *
 * The New Loads tab adds rows one click at a time, so an untouched row is a
 * normal intermediate state — not an incomplete entry. Blank rows carry no
 * load, so nothing about them belongs on the report or in completion
 * validation: a project with a single empty row is a project with no proposed
 * loads, which is a legitimate evaluation (does the existing service have
 * room?).
 *
 * Only the fields that say something about the load count. Quantity and
 * voltage arrive pre-filled with defaults, so they cannot distinguish an
 * untouched row from a deliberate one.
 */
export function isBlankProposedRow(eq: Equipment): boolean {
  return (
    !eq.equipment_name?.trim() &&
    !eq.equipment_type &&
    !eq.source &&
    eq.amps == null &&
    eq.kw == null
  );
}

/**
 * Proposed loads the preparer actually entered — blank rows removed.
 *
 * Use this everywhere a proposed load is evaluated, printed, or validated.
 * The Equipment tab itself must keep using `proposedLoads`, or a row would
 * vanish the moment it was added.
 */
export function enteredProposedLoads(equipment: Equipment[]): Equipment[] {
  return proposedLoads(equipment).filter((eq) => !isBlankProposedRow(eq));
}
